import { ChangeDetectionStrategy, Component, Injector, Input, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IBalooSyncLog, IBalooSyncLogListParameter } from '@fi-app/external-platform-integration/baloo/baloo';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiDateTimeFormatOptions } from '@fi-shared/utils';
import { BalooService } from '@fi-app/external-platform-integration/baloo/baloo.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { BalooHelperService } from '@fi-app/external-platform-integration/baloo/baloo-helper.service';

@Component({
    selector: 'baloo-sync-log-list',
    templateUrl: './baloo-sync-log-list.component.html',
    styleUrls: ['./baloo-sync-log-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'baloo-sync-log-list' },
    standalone: false,
})
export class BalooSyncLogListComponent extends FiRemoteStateFullDataSource<IBalooSyncLog> {
    @Input() logObjectType: ObjectTypes;

    readonly defaultSortColumnName: string = 'ChangedOn';
    readonly defaultSortDirection: boolean = true;
    readonly objectType: ObjectTypes.BalooSyncLog;

    itemsListType = FiItemsListType.BalooSyncLog;

    constructor(
        injector: Injector,
        private balooService: BalooService,
        private balooHelperService: BalooHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(injector);
    }

    loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IBalooSyncLog>> {
        const parameter = this.getItemsListParameter(loadArgs) as IBalooSyncLogListParameter;
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: false,
        };
        parameter.ObjectType = this.logObjectType;
        return this.balooService.getSyncLogListData(parameter);
    }

    openDetailedLogDialog(log: IBalooSyncLog) {
        this.balooHelperService.openDetailedLogDialog(log);
    }

    hasErrors(log: IBalooSyncLog): boolean {
        return !!log.ErrorMessage;
    }

    getDateText(log: IBalooSyncLog): string {
        let text: string = this.fiFormatDateService.formatDate(log.CreatedOn, FiDateTimeFormatOptions, {
            showToday: false,
        });
        if (log.ErrorMessage) {
            text += ' Could not complete sync!';
        }
        return text;
    }

    getItemsCountText(): string {
        if (this.logObjectType === ObjectTypes.BusinessUnit) return this.appTextService.get('baloo.units_found');
        if (this.logObjectType === ObjectTypes.Role) return this.appTextService.get('baloo.roles_found');
        if (this.logObjectType === ObjectTypes.User) return this.appTextService.get('pk.log_users_found');
    }

    getItemsAddedText(): string {
        return this.appTextService.get(this.isLogUserObjectType() ? 'baloo.added_to_baloo' : 'common.added');
    }

    isLogUserObjectType(): boolean {
        return this.logObjectType === ObjectTypes.User;
    }
}
